import { forwardRef } from 'react';
import { cn } from '../../lib/cn.js';

/**
 * Labelled input with optional error state.
 *
 * Usage:
 *   <Input label="First name" {...register('first_name')} error={errors.first_name?.message} />
 */
export const Input = forwardRef(function Input({
    label, error, hint, className, required, id, ...props
}, ref) {
    const inputId = id || props.name;
    return (
        <div className={cn('w-full', className)}>
            {label && (
                <label htmlFor={inputId} className="label">
                    {label}
                    {required && <span className="text-status-danger ml-0.5">*</span>}
                </label>
            )}
            <input
                ref={ref}
                id={inputId}
                className={cn('input', error && 'border-status-danger focus:border-status-danger focus:ring-status-danger')}
                aria-invalid={error ? 'true' : undefined}
                {...props}
            />
            {error && <p className="field-error">{error}</p>}
            {!error && hint && <p className="mt-1 text-xs text-ink-subtle">{hint}</p>}
        </div>
    );
});

export const Textarea = forwardRef(function Textarea({
    label, error, hint, className, required, id, rows = 3, ...props
}, ref) {
    const inputId = id || props.name;
    return (
        <div className={cn('w-full', className)}>
            {label && (
                <label htmlFor={inputId} className="label">
                    {label}
                    {required && <span className="text-status-danger ml-0.5">*</span>}
                </label>
            )}
            <textarea
                ref={ref}
                id={inputId}
                rows={rows}
                className={cn('input resize-y', error && 'border-status-danger focus:border-status-danger focus:ring-status-danger')}
                aria-invalid={error ? 'true' : undefined}
                {...props}
            />
            {error && <p className="field-error">{error}</p>}
            {!error && hint && <p className="mt-1 text-xs text-ink-subtle">{hint}</p>}
        </div>
    );
});

export const Select = forwardRef(function Select({
    label, error, hint, className, required, id, children, ...props
}, ref) {
    const inputId = id || props.name;
    return (
        <div className={cn('w-full', className)}>
            {label && (
                <label htmlFor={inputId} className="label">
                    {label}
                    {required && <span className="text-status-danger ml-0.5">*</span>}
                </label>
            )}
            <select
                ref={ref}
                id={inputId}
                className={cn('input', error && 'border-status-danger focus:border-status-danger focus:ring-status-danger')}
                {...props}
            >
                {children}
            </select>
            {error && <p className="field-error">{error}</p>}
            {!error && hint && <p className="mt-1 text-xs text-ink-subtle">{hint}</p>}
        </div>
    );
});

/**
 * Checkbox with label + optional description.
 */
export const Checkbox = forwardRef(function Checkbox({
    label, description, error, className, id, ...props
}, ref) {
    const inputId = id || props.name;
    return (
        <div className={cn('w-full', className)}>
            <label htmlFor={inputId} className="flex items-start gap-3 cursor-pointer">
                <input
                    ref={ref}
                    type="checkbox"
                    id={inputId}
                    className="mt-0.5 h-4 w-4 rounded border-paper-border text-ink focus:ring-ink"
                    {...props}
                />
                <span className="flex-1 min-w-0">
                    <span className="text-sm text-ink font-medium">{label}</span>
                    {description && <span className="block text-xs text-ink-subtle mt-0.5">{description}</span>}
                </span>
            </label>
            {error && <p className="field-error ml-7">{error}</p>}
        </div>
    );
});
